import React from "react";

import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";

import { Row, FitWidth, Title } from "components/common";
import Tabs, { Tab } from "components/Tabs";
import PieChart from "components/Charts/PieChart";
import ProgressChart from "components/Charts/ProgressChart";

import { CHART_COLORS } from "styles/colors";
import EventsLoader from "components/Loader";
import { isDate } from "lodash";
import { useHistory } from "react-router-dom";

const getChartDataTemplate = (colors) => {
  return {
    name: "",
    labels: [],
    values: [],
    type: "pie",
    automargin: true,
    layout: { legend: { xanchor: "left", orientation: "v" } },
    text: [],
    hoverinfo: "text",
    hole: 0.85,
    textinfo: "none",
    marker: {
      line: { color: "#FFF", width: 1 },
      colors,
    },
  };
};

const getPieChartLayoutTemplate = (total) => {
  return {
    title: { text: "", font: { color: "#3D3D3D", size: 16 } },
    font: { family: "Inter", color: "#979797" },
    annotations: [
      { font: { size: 20 }, showarrow: false, text: "All", x: 0.5, y: 0.55 },
      { font: { size: 14 }, showarrow: false, text: total, x: 0.5, y: 0.45 },
    ],
    showlegend: false,
    legend: { xanchor: "center", x: 0.45, y: -0.2, orientation: "h" },
    margin: { l: 16, r: 24, t: 24, b: 32, pad: 2 },
    hovermode: "closest",
    hoverlabel: {
      bgcolor: "#000",
      bordercolor: "#000",
      font: { color: "#fff", family: "Inter", size: 12 },
    },
    clickmode: "select+event",
    dragmode: "select",
  };
};

const getChartTooltip = (label, by, totalCount) => {
  const percent = Math.round((by.transactionByCount / totalCount) * 100);

  return `${label}: ${by.transactionByName} <br>\nCount: ${by.transactionByCount} <br>\nPercentage: ${percent}%`;
};

const getProgressData = (id, label, count, totalCount, atRiskCount) => {
  const value = Math.round((count / totalCount) * 100);

  return {
    id,
    label,
    value,
    count,
    atRiskCount,
  };
};

const ALL = "All";
const STATUS = "Status";
const TYPE = "Type";

const getChartData = (counts, prefix) => {
  if (!(Array.isArray(counts) && counts.length > 0)) return;

  const total = counts.filter(
    ({ transactionByName }) => transactionByName === ALL
  )[0].transactionByCount;
  const labels = [];
  const values = [];
  const text = [];
  const progress = [];

  counts
    .filter(({ transactionByName }) => transactionByName !== ALL)
    .forEach((item) => {
      const {
        transactionById: id,
        transactionByName: name,
        transactionByCount: count,
        atRiskCount: atRiskCount,
      } = item;

      labels.push(name);
      values.push(count);
      text.push(getChartTooltip(prefix, item, total));
      progress.push(getProgressData(id, name, count, total, atRiskCount));
    });

  const chartData = { labels, values, text, progress, total };
  return chartData;
};

const Chart = ({ name, counts, colors, onClick }) => {
  const { progress, total, ...restData } = getChartData(counts, name);
  const chartData = { ...getChartDataTemplate(colors), ...restData };
  const handleProgressClick = (row) => {
    onClick({ row, name });
  }
  return (
    <Row>
      <Box
        sx={{
          display: { xs: "block", md: "none", lg: "block" },
          flex: { xs: 1, md: 2, lg: 2, xl: 3 },
        }}
      >
        <PieChart
          data={[chartData]}
          layout={getPieChartLayoutTemplate(total)}
        />
      </Box>
      <Box sx={{ flex: { xs: 12, md: 8, lg: 8, xl: 7 } }}>
        <ProgressChart progress={progress} colors={colors} onClick={handleProgressClick} />
      </Box>
    </Row>
  );
};


const TransactionCharts = ({ title, transactionCounts, isLoading }) => {
  const history = useHistory()
  if (!transactionCounts) return null;

  const { byStatus, byType } = transactionCounts;

  const handleClick = (props) => {
    if (title === "Transactions") {
      history.push(`/transaction?filterBy=${props.name}&id=${props?.row?.id}`)
    } else {
      history.push(`/pre-transaction?filterBy=${props.name}&id=${props?.row?.id}`)
    }
  }

  return (
    <FitWidth>
      <Title>{title}</Title>
      {isLoading ? (
        <>
          <EventsLoader />
        </>
      ) : (
        <>
          <Paper padding={"1em"} elevation={2}>
            <Tabs>
              <Tab
                label={"By Status"}
                component={
                  byStatus.length > 0 ? (
                    <Chart
                      name={STATUS}
                      counts={byStatus}
                      colors={CHART_COLORS.STATUS}
                      onClick={handleClick}
                    />
                  ) : null
                }
              />
              <Tab
                label={"By Type"}
                component={
                  byType.length > 0 ? (
                    <Chart
                      name={TYPE}
                      counts={byType}
                      colors={CHART_COLORS.TYPE}
                      onClick={handleClick}
                    />
                  ) : null
                }
              />
            </Tabs>
          </Paper>
        </>
      )}
    </FitWidth>
  );
};

export default TransactionCharts;
